import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Stack,
  Chip,
  Avatar,
  CircularProgress,
  Divider,
  Alert,
} from '@mui/material';
import { FaExchangeAlt, FaUserGraduate } from 'react-icons/fa';
import { CohortStudent, CohortWithDetails } from 'src/types/cohort.types';

interface BulkTransferModalProps {
  open: boolean;
  onClose: () => void;
  selectedStudents: CohortStudent[];
  currentCohort: CohortWithDetails | null;
  availableCohorts: CohortWithDetails[];
  onConfirmBulkMove: (studentIds: string[], fromCohortId: string, toCohortId: string) => Promise<void>;
  isSubmitting?: boolean;
}

export function BulkTransferModal({
  open,
  onClose,
  selectedStudents,
  currentCohort,
  availableCohorts = [],
  onConfirmBulkMove,
  isSubmitting = false,
}: BulkTransferModalProps) {
  const [targetCohortId, setTargetCohortId] = useState<string>('');

  const filteredCohorts = availableCohorts.filter((c) => c.id !== currentCohort?.id);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!targetCohortId || !currentCohort || selectedStudents.length === 0) return;

    const studentIds = selectedStudents.map((s) => s.studentId);
    await onConfirmBulkMove(studentIds, currentCohort.id, targetCohortId);
    setTargetCohortId('');
    onClose();
  };

  const targetCohort = availableCohorts.find((c) => c.id === targetCohortId);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          p: 1,
        },
      }}
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                bgcolor: '#EFF6FF',
                color: '#2563EB',
                display: 'grid',
                placeItems: 'center',
              }}
            >
              <FaExchangeAlt size={18} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={800} color="#0F172A">
                Batch Transfer Scholars ({selectedStudents.length})
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Move multiple selected scholars to a different cohort section
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>

        <DialogContent dividers sx={{ my: 1 }}>
          <Stack spacing={2.5}>
            <Alert severity="info" sx={{ borderRadius: 2, fontSize: '0.8125rem' }}>
              Transferring scholars preserves their virtual currency balances, point history, and badges.
            </Alert>

            {/* Selected Scholars Preview */}
            <Box>
              <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                SELECTED SCHOLARS ({selectedStudents.length})
              </Typography>
              <Box
                sx={{
                  maxHeight: 130,
                  overflowY: 'auto',
                  p: 1,
                  bgcolor: '#F8FAFC',
                  borderRadius: 2,
                  border: '1px solid #E2E8F0',
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 0.75,
                }}
              >
                {selectedStudents.map((s) => (
                  <Chip
                    key={s.id}
                    avatar={
                      <Avatar sx={{ bgcolor: '#DBEAFE', color: '#1E40AF', fontSize: '0.75rem', fontWeight: 700 }}>
                        {s.firstname?.[0]}
                      </Avatar>
                    }
                    label={`${s.firstname} ${s.lastname}`}
                    size="small"
                    sx={{ bgcolor: '#FFFFFF', border: '1px solid #E2E8F0', fontWeight: 600 }}
                  />
                ))}
              </Box>
            </Box>

            {/* Source to Destination mapping */}
            <Box
              sx={{
                p: 2,
                borderRadius: 2.5,
                bgcolor: '#F8FAFC',
                border: '1px solid #E2E8F0',
              }}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={700} display="block">
                    CURRENT SECTION
                  </Typography>
                  <Typography variant="subtitle2" fontWeight={800} color="#0F172A">
                    {currentCohort?.name || 'Current'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {currentCohort?.className || 'Grade Level'}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    bgcolor: '#FFFFFF',
                    border: '1px solid #CBD5E0',
                    display: 'grid',
                    placeItems: 'center',
                    color: '#64748B',
                  }}
                >
                  <FaExchangeAlt size={13} />
                </Box>

                <Box sx={{ flex: 1, textAlign: 'right' }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={700} display="block">
                    TARGET SECTION
                  </Typography>
                  <Typography variant="subtitle2" fontWeight={800} color="#2563EB">
                    {targetCohort?.name || 'Select Section...'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {targetCohort?.className || '---'}
                  </Typography>
                </Box>
              </Stack>
            </Box>

            {/* Target Cohort Selector */}
            <FormControl fullWidth size="medium">
              <InputLabel id="target-cohort-select-label">Select Destination Cohort</InputLabel>
              <Select
                labelId="target-cohort-select-label"
                value={targetCohortId}
                label="Select Destination Cohort"
                onChange={(e) => setTargetCohortId(e.target.value)}
                sx={{ borderRadius: 2 }}
              >
                {filteredCohorts.map((cohort) => (
                  <MenuItem key={cohort.id} value={cohort.id}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ width: '100%' }}>
                      <Typography fontWeight={700} fontSize="0.875rem">
                        {cohort.name} ({cohort.className || 'Grade Section'})
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {cohort.students?.length || 0} scholars enrolled
                      </Typography>
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={onClose} variant="outlined" sx={{ borderRadius: 2, color: '#475569', borderColor: '#CBD5E0' }}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={!targetCohortId || isSubmitting}
            sx={{
              borderRadius: 2,
              bgcolor: '#2563EB',
              color: '#FFFFFF',
              fontWeight: 700,
              px: 3,
              '&:hover': { bgcolor: '#1D4ED8' },
            }}
          >
            {isSubmitting ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              `Confirm Transfer (${selectedStudents.length} Scholars)`
            )}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
